import * as i0 from '@angular/core';
import { Injectable, NgModule, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as i1 from '@angular/common/http';
import { HttpHeaders, HttpParams, HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { ReplaySubject, BehaviorSubject, combineLatest, from } from 'rxjs';
import { __decorate } from 'tslib';
import { filter, take, distinctUntilChanged } from 'rxjs/operators';
import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
import jwt_decode from 'jwt-decode';
import * as i2 from '@angular/router';
import { lib, enc, SHA256 } from 'crypto-js';
import * as i7 from '@uoa/error-pages';

class UrlBuilder {
    _httpClient;
    _discoveryEndpoint;
    _authorizeEndpoint;
    cognitoUrls = new ReplaySubject(1);
    constructor(_httpClient) {
        this._httpClient = _httpClient;
    }
    getEndPoints(config) {
        this._discoveryEndpoint = `https://cognito-idp.${config.cognitoAwsRegion}.amazonaws.com/${config.cognitoUserPoolId}/.well-known/openid-configuration`;
        this._httpClient
            .get(this._discoveryEndpoint)
            .subscribe((res) => this.cognitoUrls.next(this.buildCognitoUrls(res, config)));
    }
    buildCognitoUrls(urls, config) {
        this._authorizeEndpoint = urls.authorization_endpoint;
        return {
            discoveryEndpoint: this._discoveryEndpoint,
            authorizeEndpoint: urls.authorization_endpoint,
            tokenEndpoint: urls.token_endpoint,
            logoutUrl: `https://${config.cognitoDomain}.auth.${config.cognitoAwsRegion}.amazoncognito.com/logout`,
            redirectUrl: config.redirectUri,
        };
    }
    buildCognitoAuthUrl(config, codeChallenge) {
        return {
            authorizeUrl: `${this._authorizeEndpoint}?client_id=${config.cognitoClientId}&response_type=code&redirect_uri=${config.redirectUri}&code_challenge=${codeChallenge.codeChallenge}&code_challenge_method=${config.codeChallengeMethod}&scope=${encodeURI(config.scopes)}`,
            codeVerifier: codeChallenge.codeVerifier,
        };
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: UrlBuilder, deps: [{ token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: UrlBuilder, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: UrlBuilder, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }]; } });

class StorageService {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: StorageService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: StorageService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: StorageService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class PkceService {
    storageService;
    challengePair$ = new BehaviorSubject(null);
    constructor(storageService) {
        this.storageService = storageService;
    }
    clearChallengeFromStorage() {
        this.storageService.removeItem('codeVerifier');
    }
    loadChallengePair() {
        this._challengeFromStorage().then((challenge) => {
            const challengePair = this.challengePair$.getValue();
            if (!challengePair && challenge) {
                this.challengePair$.next(challenge);
            }
            else {
                this.generateChallengePair();
            }
        });
    }
    generateChallengePair() {
        const codeVerifier = this._stripSymbols(lib.WordArray.random(32).toString(enc.Base64));
        const codeChallenge = this._stripSymbols(SHA256(codeVerifier).toString(enc.Base64));
        this.storageService.setItem('codeVerifier', codeVerifier);
        this.challengePair$.next({ codeChallenge, codeVerifier });
    }
    async _challengeFromStorage() {
        const codeVerifier = await this.storageService.getItem('codeVerifier');
        if (!codeVerifier)
            return null;
        return { codeChallenge: null, codeVerifier };
    }
    _stripSymbols(str) {
        return str.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: PkceService, deps: [{ token: StorageService }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: PkceService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: PkceService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: StorageService }]; } });

class CognitoConfigService {
    cognitoAwsRegion;
    cognitoUserPoolId;
    cognitoDomain;
    cognitoClientId;
    redirectUri;
    logoutUri;
    codeChallengeMethod;
    scopes;
    bearerTokenUrlFilter;
    errorPage = 'error';
    navigateWithoutToken = true;
    useRefreshToken = true;
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: CognitoConfigService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: CognitoConfigService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: CognitoConfigService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

let AuthService = class AuthService {
    _httpClient;
    _router;
    _urlBuilder;
    _pkceService;
    _cognitoConfig;
    _storageService;
    _bypassErrorService;
    _oAuth2Urls$ = new ReplaySubject(1);
    _cognitoUrls$ = new ReplaySubject(1);
    loggedIn$ = new BehaviorSubject(false);
    userInfo$ = new BehaviorSubject(undefined);
    constructor(_httpClient, _router, _urlBuilder, _pkceService, _cognitoConfig, _storageService, _bypassErrorService) {
        this._httpClient = _httpClient;
        this._router = _router;
        this._urlBuilder = _urlBuilder;
        this._pkceService = _pkceService;
        this._cognitoConfig = _cognitoConfig;
        this._storageService = _storageService;
        this._bypassErrorService = _bypassErrorService;
        this._cognitoUrls$ = this._urlBuilder.cognitoUrls;
        this._urlBuilder.getEndPoints(this._cognitoConfig);
        combineLatest([this._pkceService.challengePair$, this._cognitoUrls$])
            .pipe(filter(([challengePair, urls]) => !!challengePair), untilDestroyed(this))
            .subscribe(([pair, url]) => this._oAuth2Urls$.next(this._urlBuilder.buildCognitoAuthUrl(this._cognitoConfig, pair)));
    }
    async hasTokenExpired() {
        const expiresAt = await this._storageService.getItem('expiresAt');
        if (!expiresAt)
            return true;
        // be careful expiresAt is in seconds and Date.now() in milliseconds
        return expiresAt && Date.now() >= Number(expiresAt) - 10000;
    }
    async obtainValidAccessToken() {
        let validToken = true;
        if (await this.hasTokenExpired()) {
            // check if we have a refreshToken
            const refreshToken = await this._storageService.getItem('refreshToken');
            if (refreshToken && this._cognitoConfig.useRefreshToken) {
                await this._exchangeRefreshTokenForTokens(refreshToken);
                validToken = true;
            }
            else {
                validToken = false; // invalid token and no refresh token
            }
        }
        let token;
        if (validToken) {
            token = await this._storageService.getItem('accessToken');
        }
        if (!token) {
            this.loggedIn$.next(false);
            if (this._cognitoConfig.navigateWithoutToken) {
                return this._navigateToAuthUrl();
            }
        }
        else {
            this.loggedIn$.next(true);
        }
        return token;
    }
    async exchangeCodeForTokens(code) {
        this._pkceService.loadChallengePair();
        const urls = await this._cognitoUrls$.pipe(take(1)).toPromise();
        const oAthUrls = await this._oAuth2Urls$.pipe(take(1)).toPromise();
        const headers = new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded',
        });
        const body = new HttpParams()
            .set('client_id', this._cognitoConfig.cognitoClientId)
            .set('redirect_uri', urls.redirectUrl)
            .set('code', code)
            .set('code_verifier', oAthUrls.codeVerifier)
            .set('grant_type', 'authorization_code');
        const response = await this._httpClient.post(urls.tokenEndpoint, body.toString(), { headers }).toPromise();
        if (response) {
            await this._storeTokens(response);
        }
        this._pkceService.clearChallengeFromStorage();
        this.returnToTargetRoute();
    }
    logout() {
        this._cognitoUrls$.subscribe((urls) => {
            this._clearOurTokens();
            this._clearTargetUrl();
            window.open(`${urls.logoutUrl}?client_id=${this._cognitoConfig.cognitoClientId}&logout_uri=${this._cognitoConfig.logoutUri}`, '_self');
        });
    }
    returnToTargetRoute() {
        this._storageService.getItem('targetUrl').then((res) => {
            const url = res ? res : '/';
            this._router.navigateByUrl(url);
        });
    }
    async getUserInfos() {
        const idToken = await this._storageService.getItem('idToken');
        const userInfos = {};
        if (idToken) {
            const decodedToken = jwt_decode(idToken);
            userInfos.upi = decodedToken['identities'][0].userId;
            userInfos.userId = decodedToken['custom:EmpID'];
            userInfos.firstName = decodedToken['given_name'];
            userInfos.lastName = decodedToken['family_name'];
            userInfos.email = decodedToken['email'];
            userInfos.groups = decodedToken['custom:Groups'] ? decodedToken['custom:Groups']?.slice(1, -1).split(',') : [];
            this.userInfo$.next(userInfos);
        }
        else {
            this.userInfo$.next(undefined);
        }
        return userInfos;
    }
    ////////////// ######## PRIVATE ######## \\\\\\\\\\\\\\\
    _navigateToAuthUrl() {
        this._pkceService.generateChallengePair();
        this._oAuth2Urls$.pipe(take(1)).subscribe((urls) => window.open(urls.authorizeUrl, '_self'));
    }
    async _storeTokens(tokens) {
        // store auth data in storage
        await this._storageService.setItem('refreshToken', tokens.refresh_token);
        await this._storageService.setItem('accessToken', tokens.access_token);
        await this._storageService.setItem('idToken', tokens.id_token);
        await this._storageService.setItem('expiresAt', Date.now() + tokens.expires_in * 1000);
        await this.getUserInfos();
    }
    _clearOurTokens() {
        this._storageService.removeItem('refreshToken');
        this._storageService.removeItem('accessToken');
        this._storageService.removeItem('idToken');
        this._storageService.removeItem('expiresAt');
        this._pkceService.clearChallengeFromStorage();
    }
    _clearTargetUrl() {
        this._storageService.removeItem('targetUrl');
    }
    async _exchangeRefreshTokenForTokens(refreshToken) {
        const headers = new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded',
        });
        const body = new HttpParams()
            .set('refresh_token', refreshToken)
            .set('client_id', this._cognitoConfig.cognitoClientId)
            .set('grant_type', 'refresh_token');
        const urls = await this._cognitoUrls$.pipe(take(1)).toPromise();
        this._bypassErrorService.bypassError(urls.tokenEndpoint, [400, 401, 403, 404]);
        const tokens = await this._httpClient
            .post(urls.tokenEndpoint, body.toString(), { headers })
            .toPromise()
            .catch((_) => {
            this._clearOurTokens();
            this._navigateToAuthUrl();
        });
        const allTokens = { ...tokens, refresh_token: refreshToken };
        if (tokens) {
            await this._storeTokens(allTokens);
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: AuthService, deps: [{ token: i1.HttpClient }, { token: i2.Router }, { token: UrlBuilder }, { token: PkceService }, { token: CognitoConfigService }, { token: StorageService }, { token: i7.BypassErrorService }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: AuthService, providedIn: 'root' });
};
AuthService = __decorate([
    UntilDestroy()
], AuthService);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: AuthService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: i2.Router }, { type: UrlBuilder }, { type: PkceService }, { type: CognitoConfigService }, { type: StorageService }, { type: i7.BypassErrorService }]; } });

class AuthInterceptor {
    _injector;
    cognitoConfig;
    _authService;
    constructor(_injector, cognitoConfig) {
        this._injector = _injector;
        this.cognitoConfig = cognitoConfig;
    }
    intercept(req, next) {
        const tokenFilter = this._needTokenType(req, this.cognitoConfig.bearerTokenUrlFilter);
        console.debug('Url to check for token', req.url);
        console.debug('Need Token', tokenFilter);
        if (tokenFilter.hasUrl) {
            return from(this._handleAccessToken(req, next, tokenFilter.optional));
        }
        return next.handle(req);
    }
    async _handleAccessToken(req, next, optionalToken) {
        this._authService = this._injector.get(AuthService);
        if (optionalToken !== true || (await this._authService.hasTokenExpired()) === false) {
            const token = 'Bearer ' + (await this._authService.obtainValidAccessToken());
            req = this._appendAuthHeader(req, token);
        }
        return next.handle(req).toPromise();
    }
    _appendAuthHeader(req, formattedToken) {
        const headers = req.headers.append('Authorization', formattedToken);
        return req.clone({ headers });
    }
    _needTokenType(req, tokenFilters) {
        let result = false;
        let optional = false;
        tokenFilters.forEach((keyWord) => {
            if (req.url.includes(keyWord.url)) {
                if (keyWord.optional === true) {
                    optional = true;
                    if (keyWord.publicPaths && keyWord.publicPaths.length > 0) {
                        optional = this._matchUrl(req.url, keyWord.url, keyWord.publicPaths);
                    }
                    else if (keyWord.protectedPaths && keyWord.protectedPaths.length > 0) {
                        optional = !this._matchUrl(req.url, keyWord.url, keyWord.protectedPaths);
                    }
                }
                result = true;
            }
        });
        return { hasUrl: result, optional };
    }
    _matchUrl(requestUrl, baseUrl, paths) {
        for (let k = 0; k < paths.length; k++) {
            const url = `${baseUrl}/${paths[k]}`;
            if (requestUrl.includes(url)) {
                return true;
            }
        }
        return false;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: AuthInterceptor, deps: [{ token: i0.Injector }, { token: CognitoConfigService }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: AuthInterceptor });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: AuthInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i0.Injector }, { type: CognitoConfigService }]; } });

class AuthModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: AuthModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.3", ngImport: i0, type: AuthModule, imports: [CommonModule, HttpClientModule] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: AuthModule, providers: [{ provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true }], imports: [CommonModule, HttpClientModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: AuthModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [CommonModule, HttpClientModule],
                    providers: [{ provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true }],
                    exports: [],
                }]
        }] });

class LoginService {
    _authService;
    _storageService;
    _router;
    _cognitoConfig;
    loggedIn$;
    userInfo$;
    constructor(_authService, _storageService, _router, _cognitoConfig) {
        this._authService = _authService;
        this._storageService = _storageService;
        this._router = _router;
        this._cognitoConfig = _cognitoConfig;
        this.loggedIn$ = this._authService.loggedIn$.pipe(distinctUntilChanged());
        this.userInfo$ = this._authService.userInfo$.pipe(distinctUntilChanged());
    }
    async isAuthenticated() {
        const token = await this._storageService.getItem('accessToken');
        const authenticated = !(await this._authService.hasTokenExpired()) && !!token;
        this._authService.loggedIn$.next(authenticated);
        if (authenticated) {
            this.getUserInfo();
        }
        return authenticated;
    }
    async doLogin(targetReturnUrl) {
        this._storageService.setItem('targetUrl', targetReturnUrl);
        return await this._authService.obtainValidAccessToken();
    }
    /**
     *  It is to handle login success case only to use returned code to get token, not Authentication.
     *  So it should always return true or redirect if there is an error from server.
     */
    async loginSuccess(state) {
        const code = state.root.queryParamMap.get('code');
        const error = state.root.queryParamMap.get('error');
        const errorDescription = state.root.queryParamMap.get('error_description');
        if (code) {
            await this._authService.exchangeCodeForTokens(code);
            console.debug('code exchange happened successfully');
        }
        else if (error) {
            console.debug('Auth error Code: ', error);
            console.debug('Auth Error Description: ', errorDescription);
            return Promise.resolve(this._router.createUrlTree([`${this._cognitoConfig.errorPage}/403`]));
        }
        return Promise.resolve(true);
    }
    async getUserInfo() {
        return await this._authService.getUserInfos();
    }
    logout() {
        this._authService.logout();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: LoginService, deps: [{ token: AuthService }, { token: StorageService }, { token: i2.Router }, { token: CognitoConfigService }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: LoginService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: LoginService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: AuthService }, { type: StorageService }, { type: i2.Router }, { type: CognitoConfigService }]; } });

class AuthGuardService {
    _loginService;
    constructor(_loginService) {
        this._loginService = _loginService;
    }
    async canActivate(next, state) {
        const isLoginSuccess = await this._loginService.loginSuccess(state);
        if (true !== isLoginSuccess) {
            return isLoginSuccess;
        }
        else {
            return !!(await this._loginService.doLogin(state.url));
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: AuthGuardService, deps: [{ token: LoginService }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: AuthGuardService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: AuthGuardService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: LoginService }]; } });
const AuthGuard = (next, state) => {
    return inject(AuthGuardService).canActivate(next, state);
};

class LoginSuccessGuardService {
    _loginService;
    constructor(_loginService) {
        this._loginService = _loginService;
    }
    async canActivate(next, state) {
        return await this._loginService.loginSuccess(state);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: LoginSuccessGuardService, deps: [{ token: LoginService }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: LoginSuccessGuardService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.3", ngImport: i0, type: LoginSuccessGuardService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: LoginService }]; } });
const LoginSuccessGuard = (next, state) => {
    return inject(LoginSuccessGuardService).canActivate(next, state);
};

/*
 * Public API Surface of auth
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AuthGuard, AuthModule, CognitoConfigService, LoginService, LoginSuccessGuard, StorageService };
